import * as React from "react";
import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { theme } from "../../utils/theme";
import { AppText } from "../UI";
import { PhoenixIcon } from "../UI/Phoenix";
import { xIcon } from "src/images/NewDesign";
import { usePendo } from "src/utils/usePendo";
import { question } from "src/images/NewDesign/";

interface HelpModalProps {
  showHelpModal: boolean;
  setShowHelpModal: (showHelpModal: boolean) => void;
}

export const HelpCenterOption = ({
  title,
  description,
  selected,
  onClick,
  disabled,
  id,
}: {
  id: string;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
}) => {
  return (
    <OptionDiv id={id} key={title} disabled={disabled} selected={selected} onClick={!disabled ? onClick : undefined}>
      <AppText
        fontSize={12}
        fontWeight={600}
        color={selected ? theme.WHITE_COLOR : disabled ? theme.NEUTRAL500 : theme.PRIMARY600}
      >
        {title}
      </AppText>
      <AppText
        fontSize={10}
        fontWeight={400}
        color={selected ? theme.WHITE_COLOR : disabled ? theme.NEUTRAL500 : theme.NEUTRAL300}
      >
        {description}
      </AppText>
    </OptionDiv>
  );
};

const HelpModal = ({ showHelpModal, setShowHelpModal }: HelpModalProps) => {
  const [isHubSpotCenterVisible, setIsHubSpotCenterVisible] = useState<boolean>(false);
  const [isPendoCenterVisible, setIsPendoCenterVisible] = useState<boolean>(false);

  const pendo = usePendo();

  useEffect(() => {
    hideHubSpotCenter();
    return () => {
      showHubSpotCenter();
    };
  }, []);

  const toggleResourceContainer = () => {
    const resourceContainer = document.querySelector("#hubspot-messages-iframe-container");
    console.log("resourceContainer", resourceContainer);
    setIsHubSpotCenterVisible(!isHubSpotCenterVisible);

    if (resourceContainer && resourceContainer instanceof HTMLElement && !isHubSpotCenterVisible) {
      console.log("resourceContainer", resourceContainer);
      console.log("resourceContainer.style going none next", resourceContainer.style);
      // remove container from DOM
      (resourceContainer as HTMLElement).style.setProperty("display", "block", "important");
    } else if (resourceContainer && resourceContainer instanceof HTMLElement && isHubSpotCenterVisible) {
      console.log("resourceContainer", resourceContainer);
      (resourceContainer as HTMLElement).style.setProperty("display", "none", "important");
    }
  };

  const hideHubSpotCenter = () => {
    const resourceContainer = document.querySelector("#hubspot-messages-iframe-container");
    if (resourceContainer && resourceContainer instanceof HTMLElement) {
      (resourceContainer as HTMLElement).style.setProperty("display", "none", "important");
    }
  };

  const showHubSpotCenter = () => {
    const resourceContainer = document.querySelector("#hubspot-messages-iframe-container");
    if (resourceContainer && resourceContainer instanceof HTMLElement) {
      (resourceContainer as HTMLElement).style.setProperty("display", "block", "important");
    }
  };

  return (
    <SlideInDiv>
      <ModalContent>
        <DarkDiv
          onClick={() => {
            if (!isHubSpotCenterVisible && !isPendoCenterVisible) {
              setShowHelpModal(!showHelpModal);
            }
          }}
        />
        <ModalHead>
          <AppText fontSize={16} fontWeight={500} lineHeight={22} textAlign="center" style={{ width: "100%" }}>
            Resources
          </AppText>
          <PhoenixIcon svg={xIcon} size={24} pointer onClick={() => setShowHelpModal(!showHelpModal)} />
        </ModalHead>
        <ButtonsDiv>
          <HelpCenterOption
            id="open-pendo-resource-center"
            title={"Resource Center"}
            description={"Review the latest Pendo Guides and product announcements."}
            selected={isPendoCenterVisible}
            onClick={() => {
              setIsPendoCenterVisible(!isPendoCenterVisible);
            }}
            disabled={false}
          />
          <HelpCenterOption
            id="open-hubspot-customer-center"
            key={"customer"}
            title={"Customer Center"}
            description={"Speak with our support team to resolve any issues."}
            selected={isHubSpotCenterVisible}
            onClick={() => {
              toggleResourceContainer();
            }}
            disabled={false}
          />
          {isPendoCenterVisible && (
            <PendoDiv id="pendo-resource-center">
              <PhoenixIcon svg={question} size={42} color={theme.NEUTRAL100} variant="brand" pointer />
            </PendoDiv>
          )}
        </ButtonsDiv>
      </ModalContent>
    </SlideInDiv>
  );
};

const PendoDiv = styled.div`
  display: block;
  position: fixed;
  left: 568px;
  top: 24px;
  width: 58px;
  height: 58px;
  z-index: 10;
  background-color: ${theme.PRIMARY500};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalHead = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px;
  /* margin-bottom: 32px; */
`;

const ButtonsDiv = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const OptionDiv = styled.div<{ selected: boolean; disabled: boolean }>`
  background: ${(props) => (props.selected ? theme.PRIMARY600 : props.disabled ? theme.NEUTRAL300 : theme.WHITE_COLOR)};
  border: 1px solid
    ${(props) => (props.disabled ? theme.NEUTRAL500 : props.selected ? theme.PRIMARY500 : theme.NEUTRAL300)};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 88px;
  width: 100%;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
`;
const ModalContent = styled.div`
  height: auto;
  /* max-height: 70vh; */
  width: 100%;
  padding: 8px;
  z-index: 9;
`;

const ModalHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  gap: 4px;
  justify-content: center;
  padding-top: 40px;
  padding-bottom: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 40px 40px 40px;
  position: relative;
  width: 100%;
  z-index: 1;
`;

const ModalFooter = styled.div`
  align-items: center;
  border-top: solid 1px ${theme.NEUTRAL200};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 40px;
  width: 100%;
`;

const HeaderTitle = styled(AppText)`
  font-size: 14px;
  font-weight: 600;
  /* margin-bottom: 27px; */
`;

const slideInAnimation = keyframes`
  0% { margin-left: 0px; width: 0px; }
  100% { margin-left: 0px; width: 472px; }
`;

const transitionBackground = keyframes`
  0% { background-color: rgba(0, 0, 0, 0); left: 72px; }
  100% { background-color: rgba(0, 0, 0, 0.5); left: calc(472px + 72px); }
`;

const DarkDiv = styled.div`
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: calc(472px + 72px);
  right: 0px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 7;
  animation-name: ${transitionBackground};
  animation-duration: 400ms;
  animation-timing-function: ease-in-out;
`;

const SlideInDiv = styled.div`
  position: absolute;
  left: 72px;
  width: 472px;
  min-height: 100vh;
  overflow: hidden;
  animation-name: ${slideInAnimation};
  animation-duration: 400ms;
  z-index: 8;
  background-color: ${theme.WHITE_COLOR};
  animation-timing-function: ease-in-out;
`;

export { HelpModal };
